// @flow
import { Cookies } from 'react-cookie';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';

import * as Actions from './actions';
import * as ActionsType from './constants';
import { apiServiceUrl, LOGIN_PATH } from '../../constants';
import {
  fetchJSON,
  fetchBasicPostMethod,
  GetErrorMessage,
  GetErrorMessageFromStatus,
  fetchPostMethodWithFormData,
  fetchJSONWithoutData,
  fetchGetMethod,
} from '../../helpers/api';
import { getLoggedInUser } from '../../helpers/authUtils';

/**
 * Sets the session
 * @param {*} user
 */
export const setSession = (user) => {
  let cookies = new Cookies();
  if (user) cookies.set('user', JSON.stringify(user), { path: '/' });
  else cookies.remove('user', { path: '/' });
};
/**
 * Login the user
 * @param {*} payload - username and password
 */
function* login({ payload: { username, password } }) {
  try {
    const body = { login: username, password: password };
    const response = yield call(
      fetchBasicPostMethod,
      `${apiServiceUrl}/access/login`,
      username,
      password,
      body
    );
    if (response.status === 200) {
      if (response.body.status === 200) {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectUrl = urlParams.get('redirectUrl');

        // Store redirectUrl in session along with user data
        const userDataWithRedirect = {
          ...response.body,
          redirectUrl: redirectUrl,
        };

        setSession(userDataWithRedirect);
        yield put(Actions.loginUserSuccess(userDataWithRedirect));
      } else {
        throw { status: response.body.status };
      }
    } else {
      throw response;
    }
  } catch (error) {
    const apiMessage = GetErrorMessage(error);
    const message =
      apiMessage && apiMessage !== 'Bad request' ? apiMessage : 'Invalid email or password.';
    yield put(Actions.loginUserFailed(message));
    setSession(null);
  }
}

/**
 * Logout the user
 * @param {*} param0
 */
function* logout({ payload: { history } }) {
  try {
    setSession(null);
    yield call(() => {
      history.push(LOGIN_PATH);
    });
  } catch (error) {}
}

function* validateRegistrationEmail({ payload: { token } }) {
  const option = {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + token,
      Accept: 'application/json',
    },
  };
  const defaultErrorMessage = 'An unexpected error has occurred.';
  try {
    const response = yield call(
      fetchJSON,
      `${apiServiceUrl}/access/ValidateRegistrationEmail`,
      option
    );

    if (response.status === 200) {
      yield put(Actions.validateRegistrationEmailSuccess());
    } else {
      const { message } = yield response.data
        .then((response) => {
          return { message: response.message };
        })
        .catch(() => ({ message: defaultErrorMessage }));
      yield put(Actions.validateRegistrationEmailFailed(response.status, message));
    }
  } catch (error) {
    yield put(Actions.validateRegistrationEmailFailed(500, defaultErrorMessage));
  }
}

function* registerUserEmail({ payload: { email } }) {
  const loggedInUser = getLoggedInUser();
  const headers = { 'Content-Type': 'application/json' };
  if (loggedInUser?.token) {
    headers.Authorization = `Bearer ${loggedInUser.token}`;
  }
  const options = {
    body: JSON.stringify({ email }),
    method: 'POST',
    headers,
  };

  try {
    const response = yield call(fetchJSON, `${apiServiceUrl}/access/RegisterEmail`, options);

    if (response.status === 200) {
      const { status, message } = yield response.data
        .then((response) => {
          return {
            status: response.status ?? response.Status,
            message: response.description ?? response.Description ?? response.message ?? response.Message,
          };
        })
        .catch(() => ({ status: 500 }));
      if (status === 200) {
        yield put(Actions.registerUserEmailSuccess());
      } else {
        const defaultMessage = GetErrorMessageFromStatus({ status });
        yield put(Actions.registerUserEmailFailed(message ?? defaultMessage));
      }
    } else {
      const { message } = yield response.data
        .then((response) => {
          return {
            message: response.description ?? response.Description ?? response.message ?? response.Message,
          };
        })
        .catch(() => ({ status: 500 }));
      const defaultMessage = GetErrorMessageFromStatus(response);
      yield put(Actions.registerUserEmailFailed(message ?? defaultMessage));
    }
  } catch (error) {
    yield put(Actions.registerUserEmailFailed(GetErrorMessageFromStatus(error)));
  }
}

/**
 * Register the user
 */
function* register({
  payload: {
    name,
    companyName,
    codeId,
    groupId,
    logo,
    country,
    state,
    city,
    zipcode,
    address,
    password,
    registrationIntent,
    token,
  },
}) {
  const safeInt = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const data = new FormData();
  data.append('Name', name);
  data.append('CompanyName', companyName ?? '');
  data.append('GroupId', String(safeInt(groupId)));
  data.append('CodeId', String(safeInt(codeId)));
  data.append('Country', country);
  data.append('State', state);
  data.append('City', city);
  data.append('ZipCode', zipcode ?? '');
  data.append('Address', address ?? '');
  data.append('Password', password);
  data.append('RegistrationIntent', registrationIntent ?? '');
  if (logo !== undefined && logo !== null && logo !== '') {
    data.append('Logo', logo);
  }
  try {
    const response = yield call(
      fetchPostMethodWithFormData,
      `${apiServiceUrl}/access/Register`,
      token,
      data
    );

    if (response.status === 200) {
      yield put(Actions.registerUserSuccess());
    } else {
      const { message } = yield response.data
        .then((response) => {
          return { message: response.message };
        })
        .catch(() => ({ status: 500 }));
      const defaultMessage = GetErrorMessageFromStatus(response);
      yield put(Actions.registerUserFailed(message ?? defaultMessage));
    }
  } catch (error) {
    yield put(Actions.registerUserFailed(GetErrorMessageFromStatus(error)));
  }
}

/**
 * forget password
 */
function* forgetPassword({ payload: { username } }) {
  const options = {
    body: JSON.stringify({ Email: username }),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  };

  try {
    const response = yield call(
      fetchJSONWithoutData,
      `${apiServiceUrl}/access/ForgotPassword`,
      options
    );
    if (response.status === 200) {
      yield put(Actions.forgetPasswordSuccess());
    } else {
      yield put(Actions.forgetPasswordFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.forgetPasswordFailed(GetErrorMessageFromStatus(error)));
  }
}

function* resetPassword({ payload: { username, password, code } }) {
  const options = {
    body: JSON.stringify({ Email: username, Code: code, Password: password }),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  };

  try {
    const response = yield call(fetchJSON, `${apiServiceUrl}/access/ResetPassword`, options);
    if (response.status === 200) {
      const { body } = yield response.data
        .then((response) => ({ body: response }))
        .catch(() => ({ status: 500 }));
      if (body.status === 200) {
        setSession(body);
        yield put(Actions.loginUserSuccess(body));
      } else {
        throw { status: body.status };
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      yield put(Actions.loginUserFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      throw response;
    }
  } catch (error) {
    yield put(Actions.loginUserFailed(GetErrorMessageFromStatus(error)));
    setSession(null);
  }
}

export function* watchLoginUser(): any {
  yield takeEvery(ActionsType.LOGIN_USER, login);
}

export function* watchLogoutUser(): any {
  yield takeEvery(ActionsType.LOGOUT_USER, logout);
}

export function* watchRegisterUser(): any {
  yield takeEvery(ActionsType.REGISTER_USER, register);
}

export function* watchRegisterUserEmail(): any {
  yield takeEvery(ActionsType.REGISTER_USER_EMAIL, registerUserEmail);
}

export function* watchValidateRegistrationEmail(): any {
  yield takeEvery(ActionsType.VALIDATE_REGISTRATION_EMAIL, validateRegistrationEmail);
}

export function* watchForgetPassword(): any {
  yield takeEvery(ActionsType.FORGET_PASSWORD, forgetPassword);
}

export function* watchResetPassword(): any {
  yield takeEvery(ActionsType.RESET_PASSWORD, resetPassword);
}

function* loadSystemAdminAccess({ payload: { token } }) {
  try {
    const response = yield call(fetchGetMethod, `${apiServiceUrl}/systemadmin/access`, token);
    if (response.status === 200) {
      const body = yield response.data.then((r) => r).catch(() => ({}));
      yield put(Actions.setSystemAdminAccess(!!body.allowed));
    } else {
      yield put(Actions.setSystemAdminAccess(false));
    }
  } catch (error) {
    yield put(Actions.setSystemAdminAccess(false));
  }
}

function* watchSystemAdminAccess(): any {
  yield takeEvery(ActionsType.REQUEST_SYSTEM_ADMIN_ACCESS, loadSystemAdminAccess);
}

function* authSaga(): any {
  yield all([
    fork(watchLoginUser),
    fork(watchLogoutUser),
    fork(watchRegisterUser),
    fork(watchRegisterUserEmail),
    fork(watchForgetPassword),
    fork(watchValidateRegistrationEmail),
    fork(watchResetPassword),
    fork(watchSystemAdminAccess),
  ]);
}

export default authSaga;
